import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackageCheck } from "lucide-react";
import { toast } from "sonner";
import { LengthsEditor } from "@/components/materials/LengthsEditor";
import { isZip } from "@/lib/zips";

export const ReceiveDialog = ({ order, onClose }) => {
  const qc = useQueryClient();
  const zip = order.kind !== "tessuti" && isZip(order);
  const [lengths, setLengths] = useState({});
  const [other, setOther] = useState(false);
  const [qty, setQty] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const [arrived, setArrived] = useState(today);
  const receive = useMutation({
    mutationFn: async (quantity) => (await api.post(`/materials/orders/${order.id}/receive`, { quantity, received_date: arrived, ...(zip ? { lengths } : {}) })).data,
    onSuccess: (o) => {
      qc.invalidateQueries({ queryKey: ["material-orders"] });
      qc.invalidateQueries({ queryKey: ["materials"] });
      toast.success(`Caricati ${num(o.received_qty)} ${o.unit} in magazzino`);
      onClose();
    },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  const confirmOther = () => {
    const v = Number(String(qty).replace(",", "."));
    if (!Number.isFinite(v) || v < 0 || qty === "") { toast.error("Scrivi la quantità arrivata"); return; }
    receive.mutate(v);
  };
  const confirmZip = () => {
    const tot = Object.values(lengths).reduce((s, q) => s + q, 0);
    if (!tot) { toast.error("Scrivi i pezzi arrivati per ogni lunghezza"); return; }
    receive.mutate(tot);
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="receive-dialog">
        <DialogHeader>
          <DialogTitle>Arrivo merce · {order.name}{order.color ? ` ${order.color}` : ""}</DialogTitle>
          <DialogDescription>
            Avevi ordinato <b>{num(order.quantity)} {order.unit}</b> da {order.supplier}. {zip ? "Scrivi quanti pezzi sono arrivati per ogni lunghezza." : "È arrivata tutta la quantità ordinata?"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-1.5">
          <Label>Data di arrivo</Label>
          <Input type="date" value={arrived} min={order.order_date} max={today} onChange={(e) => setArrived(e.target.value || today)} className="w-48" data-testid="receive-date-input" />
          <p className="text-[11px] text-muted-foreground">Se il tessuto è arrivato in un altro giorno, cambia la data: serve a calcolare i tempi di consegna del fornitore.</p>
        </div>
        {zip && (
          <div className="space-y-1.5">
            <Label>Pezzi arrivati per lunghezza</Label>
            <LengthsEditor value={{}} onChange={setLengths} testid="receive-lengths" />
          </div>
        )}
        {!zip && other && (
          <div className="space-y-1.5">
            <Label>Quantità arrivata ({order.unit})</Label>
            <Input autoFocus type="number" value={qty} onChange={(e) => setQty(e.target.value)} onKeyDown={(e) => e.key === "Enter" && confirmOther()} data-testid="receive-qty-input" />
          </div>
        )}
        <DialogFooter className="gap-2">
          {zip ? (
            <Button onClick={confirmZip} disabled={receive.isPending} data-testid="receive-zip-confirm-btn">
              {receive.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Carica in magazzino
            </Button>
          ) : !other ? (
            <>
              <Button variant="outline" onClick={() => setOther(true)} data-testid="receive-other-btn">No, è arrivata un'altra quantità</Button>
              <Button onClick={() => receive.mutate(order.quantity)} disabled={receive.isPending} data-testid="receive-all-btn">
                {receive.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PackageCheck className="mr-2 h-4 w-4" />}Sì, {num(order.quantity)} {order.unit}
              </Button>
            </>
          ) : (
            <Button onClick={confirmOther} disabled={receive.isPending} data-testid="receive-confirm-btn">
              {receive.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Carica in magazzino
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
